/**
 * API Client for Omni Context Backend
 * Handles all communication with the FastAPI backend
 */

import { toastService } from './toastService';
import authService from './authService';
import { STORAGE_KEYS } from '../config/cognito';

class OmniContextAPI {
    constructor(baseUrl) {
        // We treat an empty string VITE_API_URL as a valid value (it means "same-origin", i.e. call `/api/...`).
        // We only fall back to localhost when the env var is truly undefined.
        const envUrl = import.meta.env.VITE_API_URL;
        const resolvedBaseUrl = baseUrl !== undefined
            ? baseUrl
            : (envUrl !== undefined ? envUrl : 'http://localhost:8000');

        this.baseUrl = resolvedBaseUrl;
        this.activeWorkspaceId = null;
        console.log('API Client initialized with baseUrl:', this.baseUrl);
    }

    setActiveWorkspace(workspaceId) {
        this.activeWorkspaceId = workspaceId;
    }

    /**
     * Persist an important error as a notification in the bell
     * Fire-and-forget to avoid recursive error loops
     */
    _persistNotification(message, severity = 'warning', type = 'app_error') {
        try {
            const token = localStorage.getItem(STORAGE_KEYS.ID_TOKEN);
            if (!token) return;
            fetch(`${this.baseUrl}/api/metrics/users/me/notifications`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ message, severity, type })
            }).catch(() => { }); // Silently fail
        } catch (_) { /* ignore */ }
    }

    /**
     * Handle API errors and show appropriate toast notifications
     */
    handleError(statusCode, errorMsg) {
        // Don't show toast for 404 on chat session endpoints - they're expected

        // Handle permission denied (403)
        if (statusCode === 403) {
            // Check for LLM permission denied errors
            // Format: "Access denied: You don't have permission to use OpenAI"
            // Or: "Access denied: You don't have permission to use model gpt-4o"
            const llmProviderMatch = errorMsg.match(/Access denied: You don't have permission to use (\w+)/i);
            const llmModelMatch = errorMsg.match(/Access denied: You don't have permission to use model (.+)/i);

            if (llmModelMatch) {
                const [, model] = llmModelMatch;
                toastService.error(
                    `Access Denied: You don't have permission to use model ${model}. Contact your administrator.`,
                    5000
                );
                return;
            }

            if (llmProviderMatch) {
                const [, provider] = llmProviderMatch;
                toastService.error(
                    `Access Denied: You don't have permission to use ${provider}. Contact your administrator.`,
                    5000
                );
                return;
            }

            // Try to extract permission info from error message
            // Format: "Access denied. Required permission: write on chat"
            const match = errorMsg.match(/Required permission: (\w+) on (\w+)/i);

            if (match) {
                const [, action, resource] = match;
                toastService.error(
                    `Permission denied: You need '${action}' access to '${resource}'. Contact your administrator.`,
                    5000
                );
            } else {
                toastService.error(
                    'Permission denied. You do not have access to this resource. Contact your administrator.',
                    5000
                );
            }
            return;
        }

        // Handle authentication errors (401)
        if (statusCode === 401) {
            console.warn('API Client: 401 Error falls through. Calling signOut().');
            toastService.error('Authentication required. Please log in again.', 4000);
            authService.signOut(); // Force local logout
            return;
        }

        // Handle server errors (500) — persist to notification bell
        if (statusCode === 500) {
            toastService.error('Server error. Please try again later or contact support.', 4000);
            this._persistNotification('Server error occurred. Please try again later or contact support.', 'critical', 'server_error');
            return;
        }

        // Handle bad request (400)
        if (statusCode === 400) {
            // Show the specific error message for bad requests
            toastService.error(`Bad request: ${errorMsg}`, 4000);
            return;
        }

        // For other errors, don't show toast (let component handle if needed)
    }

    /**
     * Make HTTP request to API
     */
    async request(endpoint, options = {}, isRetry = false) {
        const url = `${this.baseUrl}${endpoint}`;

        // Get authorization token from localStorage
        const authToken = localStorage.getItem(STORAGE_KEYS.ID_TOKEN);

        // Debug: Check all possible token locations (only on first try)
        if (!isRetry) {
            const allTokens = {
                cognito_access_token: localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN),
                cognito_id_token: localStorage.getItem(STORAGE_KEYS.ID_TOKEN),
                cognito_refresh_token: localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN),
                access_token: localStorage.getItem('access_token'),
                id_token: localStorage.getItem('id_token'),
                refresh_token: localStorage.getItem('refresh_token')
            };

            console.log('Token Debug for', endpoint, ':', {
                foundIdToken: !!authToken,
                tokenLength: authToken ? authToken.length : 0,
                allTokens: Object.entries(allTokens).filter(([, value]) => value !== null),
                localStorageKeys: Object.keys(localStorage).filter(key => key.includes('token') || key.includes('cognito'))
            });
        }

        const { headers: optionHeaders, ...restOptions } = options;
        const isFormData = restOptions.body instanceof FormData;
        const config = {
            ...restOptions,
            headers: {
                ...(!isFormData && { 'Content-Type': 'application/json' }),
                ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
                ...(this.activeWorkspaceId && { 'x-workspace-id': this.activeWorkspaceId }),
                ...optionHeaders
            }
        };

        try {
            const response = await fetch(url, config);

            // Handle 401 Unauthorized - Attempt Refresh
            if (response.status === 401 && !isRetry) {
                console.log('Received 401 Unauthorized. Attempting to refresh session...');
                try {
                    const refreshResult = await authService.refreshSession();
                    if (refreshResult && refreshResult.session) {
                        console.log('Session refreshed successfully. Retrying request...');
                        // Recursive call with isRetry = true
                        return this.request(endpoint, options, true);
                    }
                } catch (refreshError) {
                    console.error('Failed to refresh session after 401:', refreshError);
                    // Fall through to normal error handling (which will sign out)
                }
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errorDetail = typeof errorData.detail === 'object'
                    ? JSON.stringify(errorData.detail)
                    : errorData.detail;
                const errorMsg = errorDetail || response.statusText;

                // Handle errors with toast notifications
                this.handleError(response.status, errorMsg, endpoint);

                // Still throw error for components that want to handle it
                throw new Error(`${response.status}: ${errorMsg}`);
            }

            // Handle different response types
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                return await response.json();
            }

            return await response.text();
        } catch (error) {
            if (error.name === 'AbortError') {
                throw error;
            }

            // Don't log 404 errors for chat session endpoints - they're expected
            if (endpoint.includes('/api/chat/sessions/') && error.message.includes('404')) {
                throw error; // Re-throw without logging
            }

            // If error doesn't have a status code, it's a network error
            if (!error.message.match(/^\d{3}:/)) {
                toastService.error('Network error. Please check your connection.');
                this._persistNotification('Network error. Please check your connection.', 'critical', 'network_error');
            }

            console.error(`API Request failed for ${endpoint}:`, error);
            throw error;
        }
    }

    /**
     * Process a user query through the multi-agent system
     */
    async processQuery(query, includeContext = true, signal = null, workspaceId = null) {
        return await this.request('/api/query', {
            method: 'POST',
            body: JSON.stringify({
                query: query,
                includeContext: includeContext,
                workspaceId: workspaceId || undefined
            }),
            signal: signal
        });
    }

    /**
     * Get all memories with optional filtering
     */
    async getMemories(filters = {}, workspaceId = null) {
        const params = new URLSearchParams();

        Object.entries(filters || {}).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                params.append(key, value);
            }
        });

        const queryString = params.toString();
        const endpoint = queryString ? `/api/memories?${queryString}` : '/api/memories';

        const options = {
            headers: {}
        };
        if (workspaceId) {
            options.headers['x-workspace-id'] = workspaceId;
        }
        return await this.request(endpoint, options);
    }

    /**
     * Get memory by ID
     */
    async getMemory(memoryId) {
        return await this.request(`/api/memories/${memoryId}`);
    }

    /**
     * Get memory details for reference linking
     */
    async getMemoryDetails(memoryId) {
        return await this.request(`/api/memories/${memoryId}`);
    }

    /**
     * Update memory entry
     */
    async updateMemory(memoryId, updateData) {
        return await this.request(`/api/memories/${memoryId}`, {
            method: 'PUT',
            body: JSON.stringify(updateData)
        });
    }

    /**
     * Delete memory entry
     */
    async deleteMemory(memoryId, workspaceId = null) {
        const options = { method: 'DELETE', headers: {} };
        if (workspaceId) {
            options.headers['x-workspace-id'] = workspaceId;
        }
        return await this.request(`/api/memories/${memoryId}`, options);
    }

    /**
     * Delete ALL memories
     */
    async deleteAllMemories(workspaceId = null) {
        const options = { method: 'DELETE', headers: {} };
        if (workspaceId) {
            options.headers['x-workspace-id'] = workspaceId;
        }
        return await this.request('/api/memories', options);
    }

    /**
     * Export memories to JSON file
     */
    async exportMemories(filters = null, workspaceId = null) {
        try {
            const url = `${this.baseUrl}/api/memories/export`;
            const authToken = localStorage.getItem(STORAGE_KEYS.ID_TOKEN);

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
                    ...(workspaceId && { 'x-workspace-id': workspaceId })
                },
                body: JSON.stringify(filters || {})
            });

            if (!response.ok) {
                throw new Error('Failed to export memories');
            }

            // Get the blob
            const blob = await response.blob();

            // Extract filename from Content-Disposition header
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `omnicontext_backup_${new Date().toISOString().replace(/[:.]/g, '-')}.json`;

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            // Create download link
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);

            return { success: true, filename };
        } catch (error) {
            console.error('Error exporting memories:', error);
            throw error;
        }
    }

    /**
     * Import memories from JSON file
     * @param {File} file - JSON file to import
     * @param {string} mode - 'merge' or 'replace'
     */
    async importMemories(file, mode = 'merge', workspaceId = null) {
        try {
            const url = `${this.baseUrl}/api/memories/import?mode=${mode}`;
            const authToken = localStorage.getItem(STORAGE_KEYS.ID_TOKEN);

            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
                    ...(workspaceId && { 'x-workspace-id': workspaceId })
                },
                body: formData
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Failed to import memories');
            }

            return await response.json();
        } catch (error) {
            console.error('Error importing memories:', error);
            throw error;
        }
    }

    /**
     * Get memory statistics
     */
    async getMemoryStats() {
        return await this.request('/api/memories/stats');
    }

    /**
     * Get user context for a query
     */
    async getUserContext(query, limit = 10) {
        const params = new URLSearchParams({ query, limit });
        return await this.request(`/api/memories/context?${params}`);
    }

    /**
     * Get all knowledge documents
     */
    async getKnowledgeDocuments() {
        return await this.request('/api/knowledge/documents');
    }

    /**
     * Get document by ID
     */
    async getDocument(docId) {
        return await this.request(`/api/knowledge/documents/${docId}`);
    }

    /**
     * Add new knowledge document
     */
    async addDocument(documentData) {
        return await this.request('/api/knowledge/documents', {
            method: 'POST',
            body: JSON.stringify(documentData)
        });
    }

    /**
     * Connect document to knowledge base
     */
    async connectDocument(docId) {
        return await this.request(`/api/knowledge/documents/${docId}/connect`, {
            method: 'PUT'
        });
    }

    /**
     * Disconnect document from knowledge base
     */
    async disconnectDocument(docId) {
        return await this.request(`/api/knowledge/documents/${docId}/disconnect`, {
            method: 'PUT'
        });
    }

    /**
     * Get knowledge base statistics
     */
    async getKnowledgeStats() {
        return await this.request('/api/knowledge/stats');
    }

    /**
     * Get all platforms
     */
    async getPlatforms() {
        return await this.request('/api/platforms');
    }

    /**
     * Get platform by ID
     */
    async getPlatform(platformId) {
        return await this.request(`/api/platforms/${platformId}`);
    }

    /**
     * Connect platform
     */
    async connectPlatform(platformId) {
        return await this.request(`/api/platforms/${platformId}/connect`, {
            method: 'PUT'
        });
    }

    /**
     * Disconnect platform
     */
    async disconnectPlatform(platformId) {
        return await this.request(`/api/platforms/${platformId}/disconnect`, {
            method: 'PUT'
        });
    }

    /**
     * Get platform statistics
     */
    async getPlatformStats() {
        return await this.request('/api/platforms/stats');
    }

    /**
     * Health check
     */
    async healthCheck() {
        return await this.request('/health');
    }

    // Supervisor API methods
    async analyzeQueryWithContext(query) {
        try {
            const response = await this.request('/api/supervisor/analyze', {
                method: 'POST',
                body: JSON.stringify({ query, includeContext: true })
            });
            return response;
        } catch (error) {
            console.error('Error analyzing query with context:', error);
            throw error;
        }
    }

    async getSupervisorStats() {
        try {
            const response = await this.request('/api/supervisor/stats');
            return response;
        } catch (error) {
            console.error('Error getting supervisor stats:', error);
            throw error;
        }
    }

    async switchLLMProvider(providerName) {
        try {
            const response = await this.request(`/api/supervisor/switch-provider?provider_name=${providerName}`, {
                method: 'POST'
            });
            return response;
        } catch (error) {
            console.error('Error switching LLM provider:', error);
            throw error;
        }
    }

    async getAvailableProviders() {
        try {
            const response = await this.request('/api/supervisor/providers');
            return response;
        } catch (error) {
            console.error('Error getting available providers:', error);
            throw error;
        }
    }

    async getDirectResponse(query) {
        try {
            const response = await this.request('/api/supervisor/direct', {
                method: 'POST',
                body: JSON.stringify({ query, includeContext: false })
            });
            return response;
        } catch (error) {
            console.error('Error getting direct response:', error);
            throw error;
        }
    }

    /**
     * Process query with supervisor using context from database
     */
    async processSupervisorQuery(query, supervisorConfig, contextEnabled = true, signal = null, sessionId = null, personalContextEnabled = true, workspaceId = null, agenticMode = false) {
        try {
            const cleanSupervisorConfig = {
                provider: supervisorConfig.provider,
                model: supervisorConfig.model
            }

            console.log('API Client - Supervisor config (server-managed keys):', {
                provider: cleanSupervisorConfig.provider,
                model: cleanSupervisorConfig.model,
                personalContextEnabled: personalContextEnabled,
                agenticMode: agenticMode,
                note: 'API keys managed by server environment variables'
            })

            const body = {
                query: query,
                supervisorConfig: cleanSupervisorConfig,
                useContext: contextEnabled,
                usePersonalContext: personalContextEnabled,
                onlyContext: true,
                sessionId: sessionId || undefined,
                workspaceId: workspaceId || undefined
            }

            if (agenticMode) {
                body.agenticMode = true
            }

            const response = await this.request('/api/supervisor/process', {
                method: 'POST',
                body: JSON.stringify(body),
                signal: signal
            });
            return response;
        } catch (error) {
            console.error('Error processing supervisor query:', error);
            console.error('Request payload:', { query, supervisorConfig, useContext: contextEnabled });
            throw error;
        }
    }

  async streamSupervisorQuery(
    query,
    supervisorConfig,
    contextEnabled = true,
    sessionId = null,
    personalContextEnabled = true,
    agenticMode = false,
    onProgress,
    signal = null,
    workspaceId = null
  ) {
    const cleanSupervisorConfig = {
      provider: supervisorConfig.provider,
      model: supervisorConfig.model
    }

    const payload = {
      query: query,
      supervisorConfig: cleanSupervisorConfig,
      useContext: contextEnabled,
      usePersonalContext: personalContextEnabled,
      onlyContext: true,
      sessionId: sessionId || undefined,
      workspaceId: workspaceId || undefined
    }

    if (agenticMode) {
      payload.agenticMode = true
    }

    const streamRequest = async (isRetry = false) => {
      const url = `${this.baseUrl}/api/supervisor/process`
      const authToken = localStorage.getItem(STORAGE_KEYS.ID_TOKEN)
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && { Authorization: `Bearer ${authToken}` })
        },
        body: JSON.stringify(payload),
        signal
      })

      if (response.status === 401 && !isRetry) {
        try {
          const refreshResult = await authService.refreshSession()
          if (refreshResult && refreshResult.session) {
            return streamRequest(true)
          }
        } catch (refreshError) {
          console.error('Failed to refresh session after 401 (stream):', refreshError)
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const errorMsg = errorData.detail || response.statusText
        this.handleError(response.status, errorMsg)
        throw new Error(`${response.status}: ${errorMsg}`)
      }

      if (!response.body) {
        throw new Error('Streaming response body is unavailable')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      const handleChunk = async (chunk) => {
        const lines = chunk
          .split('\n')
          .map(line => line.trim())
          .filter(line => line.startsWith('data:'))

        for (const line of lines) {
          const rawData = line.slice(5).trim()
          if (!rawData) continue

          const parsedData = JSON.parse(rawData)
          if (typeof onProgress === 'function') {
            onProgress(parsedData)
          }

          if (parsedData.type === 'error') {
            throw new Error(parsedData.error || 'Streaming request failed')
          }

          if (parsedData.type === 'result') {
            try {
              await reader.cancel()
            } catch {
              // no-op: stream may already be closed
            }
            return parsedData.data
          }
        }

        return null
      }

      let streamDone = false
      while (!streamDone) {
        const { value, done } = await reader.read()
        streamDone = done
        if (streamDone) break

        buffer += decoder.decode(value, { stream: true })
        const events = buffer.split('\n\n')
        buffer = events.pop() || ''

        for (const event of events) {
          const maybeResult = await handleChunk(event)
          if (maybeResult !== null) {
            return maybeResult
          }
        }
      }

      buffer += decoder.decode()
      if (buffer.trim()) {
        const maybeResult = await handleChunk(buffer)
        if (maybeResult !== null) {
          return maybeResult
        }
      }

      throw new Error('Stream ended before receiving final result')
    }

    try {
      return await streamRequest(false)
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error
      }

      if (!error.message.match(/^\d{3}:/)) {
        toastService.error('Network error. Please check your connection.')
      }
      console.error('Error streaming supervisor query:', error)
      throw error
    }
  }

    /**
     * Route query using Supervisor's intelligent analysis
     */
    async routeQueryWithSupervisor(query, supervisorConfig, availableModels, signal = null, workspaceId = null) {
        try {
            console.log('\n🔀 API CLIENT: Routing query with Supervisor')
            console.log('Query:', query.substring(0, 100) + (query.length > 100 ? '...' : ''))
            console.log('🔧 Supervisor config:', supervisorConfig)
            console.log('Available models:', availableModels.map(m => `${m.provider}/${m.model}`))
            console.log('📡 Sending request to /api/supervisor/route-query...')

            const response = await this.request('/api/supervisor/route-query', {
                method: 'POST',
                body: JSON.stringify({
                    query: query,
                    supervisorConfig: supervisorConfig,
                    availableModels: availableModels,
                    workspaceId: workspaceId || undefined
                }),
                signal: signal
            });

            console.log('Supervisor routing response received:')
            console.log('   • Selected model:', response.selectedModel)
            console.log('   • Routing analysis:', response.routingAnalysis)
            console.log('   • Processing method:', response.processingMethod)
            console.log('   • Context retrieved:', response.contextRetrieved)

            return response;

        } catch (error) {
            console.error('Error routing query with supervisor:', error);
            console.error('Request payload:', { query, supervisorConfig, availableModels });
            throw error;
        }
    }

    /**
     * Test API key for a specific LLM provider
     */
    async testApiKey(provider, apiKey) {
        return await this.request('/api/llm/test-api-key', {
            method: 'POST',
            body: JSON.stringify({
                provider: provider,
                api_key: apiKey
            })
        });
    }

    /**
     * Get available models for a specific LLM provider
     */
    async getModels(provider, apiKey = null) {
        return await this.request('/api/llm/get-models', {
            method: 'POST',
            body: JSON.stringify({
                provider: provider,
                api_key: apiKey
            })
        });
    }

    /**
     * Extend query directly to LLM for knowledge-based response
     */
    async extendQueryToLLM(query, supervisorConfig, summaryConfig = null, originalMemoryId = null, sessionId = null) {
        return this.request('/api/supervisor/extend-query', {
            method: 'POST',
            body: JSON.stringify({
                query,
                supervisorConfig,
                summaryConfig,
                originalMemoryId,
                sessionId: sessionId || undefined
            })
        });
    }

    // Chat methods
    async createChatSession(title = "New Chat", workspaceId = null) {
        return await this.request('/api/chat/sessions', {
            method: 'POST',
            body: JSON.stringify({
                title: title,
                workspaceId: workspaceId || undefined
            })
        });
    }

    async getChatSessions(limit = 50, workspaceId = null) {
        const params = new URLSearchParams({ limit: String(limit) });
        if (workspaceId) {
            params.append('workspaceId', workspaceId);
        }
        return await this.request(`/api/chat/sessions?${params.toString()}`);
    }

    async getChatSession(sessionId) {
        try {
            return await this.request(`/api/chat/sessions/${sessionId}`);
        } catch (error) {
            // Don't log 404 errors for session lookups - they're expected when sessions don't exist
            if (error.message && error.message.includes('Session not found')) {
                throw error; // Re-throw without logging
            }
            console.error(`Error getting chat session ${sessionId}:`, error);
            throw error;
        }
    }

    async addMessageToSession(sessionId, message) {
        return await this.request(`/api/chat/sessions/${sessionId}/messages`, {
            method: 'POST',
            body: JSON.stringify(message)
        });
    }

    async getSessionMessages(sessionId) {
        return await this.request(`/api/chat/sessions/${sessionId}/messages`, {
            method: 'GET'
        });
    }

    async updateChatSession(sessionId, updates) {
        return await this.request(`/api/chat/sessions/${sessionId}`, {
            method: 'PUT',
            body: JSON.stringify(updates)
        });
    }

    async deleteChatSession(sessionId) {
        return await this.request(`/api/chat/sessions/${sessionId}`, {
            method: 'DELETE'
        });
    }

    async deleteAllData() {
        return await this.request('/api/data/all', {
            method: 'DELETE'
        });
    }

    async getChatStats() {
        return await this.request('/api/chat/stats');
    }

    // Protection System API methods
    async getRestrictedTopics(activeOnly = false) {
        const params = activeOnly ? '?active_only=true' : '';
        return await this.request(`/api/protection/topics${params}`);
    }

    async getRestrictedTopic(topicId) {
        return await this.request(`/api/protection/topics/${topicId}`);
    }

    async createRestrictedTopic(topicData) {
        return await this.request('/api/protection/topics', {
            method: 'POST',
            body: JSON.stringify(topicData)
        });
    }

    async updateRestrictedTopic(topicId, topicData) {
        return await this.request(`/api/protection/topics/${topicId}`, {
            method: 'PUT',
            body: JSON.stringify(topicData)
        });
    }

    async deleteRestrictedTopic(topicId) {
        return await this.request(`/api/protection/topics/${topicId}`, {
            method: 'DELETE'
        });
    }

    async toggleTopicStatus(topicId) {
        return await this.request(`/api/protection/topics/${topicId}/toggle`, {
            method: 'POST'
        });
    }

    async analyzeQueryProtection(query) {
        return await this.request('/api/protection/analyze', {
            method: 'POST',
            body: JSON.stringify({ query })
        });
    }

    async getProtectionStats() {
        return await this.request('/api/protection/stats');
    }

    async initializeProtectionSystem() {
        return await this.request('/api/protection/initialize', {
            method: 'POST'
        });
    }

    // Clabs Configuration API methods - Single config per user
    async saveClabsConfig(configData) {
        return await this.request('/api/clabs/config', {
            method: 'POST',
            body: JSON.stringify(configData)
        });
    }

    async getClabsConfig() {
        return await this.request('/api/clabs/config');
    }

    async deleteClabsConfig() {
        return await this.request('/api/clabs/config', {
            method: 'DELETE'
        });
    }

    async testClabsConnection() {
        return await this.request('/api/clabs/config/test', {
            method: 'POST'
        });
    }

    async getClabsStats() {
        return await this.request('/api/clabs/stats');
    }

    async updateClabsEnabled(enabled) {
        // Fetch current config, update enabled state, and save back
        try {
            const response = await this.getClabsConfig();
            // Handle both nested (response.config) and direct response formats
            const configData = response?.config || response;

            if (configData && (configData.id || configData.api_url)) {
                return await this.saveClabsConfig({
                    api_url: configData.api_url,
                    selected_sources: configData.selected_sources || [],
                    available_sources: configData.available_sources || [],
                    connection_status: configData.connection_status || 'connected',
                    description: configData.description || 'User Clabs configuration',
                    tags: configData.tags || ['clabs', 'external'],
                    is_active: configData.is_active !== undefined ? configData.is_active : true,
                    enabled: enabled
                });
            }
            return { success: false, message: 'No existing Clabs configuration found' };
        } catch (error) {
            console.error('Error updating Clabs enabled state:', error);
            throw error;
        }
    }

    // RAG (Internal Knowledge Base) API methods
    async saveRagIntegrationConfig(configData) {
        return await this.request('/api/rag/integration', {
            method: 'POST',
            body: JSON.stringify(configData)
        });
    }

    async getRagIntegrationConfig() {
        return await this.request('/api/rag/integration');
    }

    async deleteRagIntegrationConfig() {
        return await this.request('/api/rag/integration', {
            method: 'DELETE'
        });
    }

    async updateRagEnabled(enabled) {
        // RAG integration config is simple, so we can upsert directly.
        try {
            return await this.saveRagIntegrationConfig({
                enabled: enabled,
                is_active: true,
                description: 'Internal Knowledge Base (RAG) configuration',
                tags: ['rag', 'internal_kb']
            });
        } catch (error) {
            console.error('Error updating RAG enabled state:', error);
            throw error;
        }
    }

    // ==================== Workspaces API ====================
    async getWorkspaces() {
        return await this.request('/api/workspaces');
    }

    async createWorkspace(data) {
        return await this.request('/api/workspaces', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    async updateWorkspace(workspaceId, data) {
        return await this.request(`/api/workspaces/${workspaceId}`, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    }

    async getWorkspaceAllUsers() {
        return await this.request('/api/workspaces/all-users');
    }

    async addWorkspaceMember(workspaceId, userSub) {
        return await this.request(`/api/workspaces/${workspaceId}/members`, {
            method: 'POST',
            body: JSON.stringify({ user_sub: userSub })
        });
    }

    async getAvailableWorkspaceUsers(workspaceId) {
        return await this.request(`/api/workspaces/${workspaceId}/available-users`);
    }

    async removeWorkspaceMember(workspaceId, userSub) {
        return await this.request(`/api/workspaces/${workspaceId}/members/${encodeURIComponent(userSub)}`, {
            method: 'DELETE'
        });
    }

    async deleteWorkspace(workspaceId) {
        return await this.request(`/api/workspaces/${workspaceId}`, {
            method: 'DELETE'
        });
    }

    // Web Search (SearXNG) configuration API methods
    async getWebSearchConfig() {
        try {
            return await this.request('/api/web-search/config');
        } catch (error) {
            console.error('Error loading Web Search config:', error);
            return null;
        }
    }

    async updateWebSearchConfig(config) {
        return await this.request('/api/web-search/config', {
            method: 'POST',
            body: JSON.stringify(config)
        });
    }

    /**
     * Query the internal knowledge base using RAG
     */
    async queryRAG(query, topK = 5, filters = null) {
        // Use test endpoint temporarily to bypass auth issues
        // TODO: Switch back to '/api/rag/query' once auth is working
        return await this.request('/api/rag/query-test', {
            method: 'POST',
            body: JSON.stringify({
                query: query,
                top_k: topK,
                filters: filters
            })
        });
    }

    /**
     * Upload a document to the internal knowledge base
     */
    async uploadRAGDocument(file, category = null, accessLevel = 'private', allowedGroups = [], workspaceId = null, signal = null) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('allowed_groups', JSON.stringify(allowedGroups));

        const params = new URLSearchParams();
        if (category) params.append('category', category);
        if (accessLevel) params.append('access_level', accessLevel);

        const queryString = params.toString();
        const endpoint = queryString ? `/api/rag/documents/upload?${queryString}` : '/api/rag/documents/upload';
        const headers = {};
        if (workspaceId && workspaceId !== 'all' && workspaceId !== 'shared') {
            headers['x-workspace-id'] = workspaceId;
        }

        // Note: FormData automatically sets the correct Content-Type with boundary
        return await this.request(endpoint, {
            method: 'POST',
            body: formData,
            headers,
            signal
        });
    }

    /**
     * List all documents in the internal knowledge base
     */
    async listRAGDocuments(workspaceId = null) {
        const headers = {};
        if (workspaceId) {
            headers['x-workspace-id'] = workspaceId;
        }
        return await this.request('/api/rag/documents', { headers });
    }

    /**
     * Delete a document from the internal knowledge base
     */
    async deleteRAGDocument(documentId) {
        return await this.request(`/api/rag/documents/${documentId}`, {
            method: 'DELETE'
        });
    }

    /**
     * Get RAG system statistics
     */
    async getRAGStats() {
        return await this.request('/api/rag/statistics');
    }

    /**
     * Check RAG system health
     */
    async checkRAGHealth() {
        return await this.request('/api/rag/health');
    }

    // Customer Database API methods
    async testCustomerDatabaseConnection(config) {
        return await this.request('/api/customer-database/test-connection', {
            method: 'POST',
            body: JSON.stringify(config)
        });
    }

    async saveCustomerDatabaseConfig(config) {
        return await this.request('/api/customer-database/save-config', {
            method: 'POST',
            body: JSON.stringify(config)
        });
    }

    // User Management API methods
    /**
     * Get user details including limits and usage
     */
    async getUserDetail(userId, fromDate = null, toDate = null) {
        let url = `/api/metrics/users/${userId}/detail`;
        const params = new URLSearchParams();
        if (fromDate) params.append('from_date', fromDate);
        if (toDate) params.append('to_date', toDate);

        const queryString = params.toString();
        if (queryString) {
            url += `?${queryString}`;
        }

        return await this.request(url);
    }

    /**
     * Update user limits
     */
    async updateUserLimits(userId, limits) {
        return await this.request(`/api/metrics/users/${userId}/limits`, {
            method: 'PUT',
            body: JSON.stringify(limits)
        });
    }

    // LLM Configuration API methods
    /**
     * Get LLM configuration for the current user
     */
    async getLLMConfig() {
        try {
            return await this.request('/api/llm-config', {
                method: 'GET'
            });
        } catch (error) {
            // Return null if configuration doesn't exist (404)
            if (error.message && error.message.includes('404')) {
                return null;
            }
            throw error;
        }
    }

    /**
     * Save LLM configuration for the current user
     */
    async saveLLMConfig(config) {
        return await this.request('/api/llm-config', {
            method: 'POST',
            body: JSON.stringify(config)
        });
    }

    /**
     * Delete LLM configuration for the current user
     */
    async deleteLLMConfig() {
        return await this.request('/api/llm-config', {
            method: 'DELETE'
        });
    }

    // ==================== Personal Context API ====================

    /**
     * Get all personal contexts for the current user
     * Returns imported conversation histories that are strictly private to the user
     */
    async getPersonalContexts() {
        try {
            return await this.request('/api/personal-context', {
                method: 'GET'
            });
        } catch (error) {
            // Return empty state if no contexts exist
            if (error.message && error.message.includes('404')) {
                return { contexts: [], stats: { total_conversations: 0, total_messages: 0, last_import: null } };
            }
            throw error;
        }
    }

    /**
     * Preview personal context file before importing
     * @param {FormData} formData - FormData containing the file and provider info
     */
    async previewPersonalContext(formData) {
        const url = `${this.baseUrl}/api/personal-context/preview`;
        const token = localStorage.getItem(STORAGE_KEYS.ID_TOKEN);

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                        resolve(JSON.parse(xhr.responseText));
                    } catch {
                        reject(new Error('Invalid response format'));
                    }
                } else {
                    try {
                        const error = JSON.parse(xhr.responseText);
                        reject(new Error(error.detail || error.message || 'Preview failed'));
                    } catch {
                        reject(new Error(`Preview failed with status ${xhr.status}`));
                    }
                }
            });

            xhr.addEventListener('error', () => {
                reject(new Error('Network error during preview'));
            });

            xhr.open('POST', url);
            if (token) {
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            }
            xhr.send(formData);
        });
    }

    /**
     * Import personal context from an external provider
     * @param {FormData} formData - FormData containing the file, provider info, and optional selected_indices
     * @param {Function} onProgress - Optional progress callback
     */
    async importPersonalContext(formData, onProgress) {
        const url = `${this.baseUrl}/api/personal-context/import`;
        const token = localStorage.getItem(STORAGE_KEYS.ID_TOKEN);

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable && onProgress) {
                    const percent = Math.round((event.loaded / event.total) * 100);
                    onProgress(percent);
                }
            });

            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                        resolve(JSON.parse(xhr.responseText));
                    } catch {
                        resolve({ success: true });
                    }
                } else {
                    try {
                        const error = JSON.parse(xhr.responseText);
                        reject(new Error(error.detail || error.message || 'Import failed'));
                    } catch {
                        reject(new Error(`Import failed with status ${xhr.status}`));
                    }
                }
            });

            xhr.addEventListener('error', () => {
                reject(new Error('Network error during import'));
            });

            xhr.open('POST', url);
            if (token) {
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            }
            xhr.send(formData);
        });
    }

    /**
     * Delete a specific personal context import
     * @param {string} contextId - The ID of the context to delete
     */
    async deletePersonalContext(contextId) {
        return await this.request(`/api/personal-context/${contextId}`, {
            method: 'DELETE'
        });
    }

    /**
     * Delete all personal contexts for the current user
     */
    async deleteAllPersonalContexts() {
        return await this.request('/api/personal-context', {
            method: 'DELETE'
        });
    }

    /**
     * Get personal context statistics for the current user
     */
    async getPersonalContextStats() {
        return await this.request('/api/personal-context/stats', {
            method: 'GET'
        });
    }

    /**
     * Get conversations from personal context with optional filtering
     * @param {Object} options - Query options
     * @param {string} options.importId - Filter by import ID
     * @param {string} options.search - Search term
     * @param {number} options.skip - Pagination offset
     * @param {number} options.limit - Maximum results
     */
    async getPersonalConversations({ importId, search, skip = 0, limit = 50 } = {}) {
        const params = new URLSearchParams();
        if (importId) params.append('import_id', importId);
        if (search) params.append('search', search);
        params.append('skip', skip.toString());
        params.append('limit', limit.toString());

        return await this.request(`/api/personal-context/conversations?${params.toString()}`, {
            method: 'GET'
        });
    }

    /**
     * Get a specific conversation with all its messages
     * @param {string} conversationId - The conversation ID
     */
    async getPersonalConversationDetail(conversationId) {
        return await this.request(`/api/personal-context/conversations/${conversationId}`, {
            method: 'GET'
        });
    }

    // ==================== Backup API ====================

    /**
     * Get list of backups
     */
    async getAllBackups() {
        return await this.request('/api/backups', {
            method: 'GET'
        });
    }

    /**
     * Get Full backups
     */
    async getFullBackups() {
        return await this.request('/api/backups/full', {
            method: 'GET'
        });
    }

    /**
     * Get Incremental backups
     */
    async getIncrementalBackups() {
        return await this.request('/api/backups/incremental', {
            method: 'GET'
        });
    }

    /**
     * Get a backup's detailed info
     */
    async getBackupInfo(backupName, backupType) {
        return await this.request(`/api/backups/${encodeURIComponent(backupType)}/${encodeURIComponent(backupName)}`, {
            method: 'GET'
        });
    }

    /**
     * Get backup schedule for a type of backup
     */
    async getBackupSchedule(backupType) {
        return await this.request(`/api/backups/${encodeURIComponent(backupType)}/schedule`, {
            method: 'GET'
        });
    }

    /**
     * Create a new backup of specified type
     */
    async createBackup(backupType) {
        return await this.request(`/api/backups/${encodeURIComponent(backupType)}`, {
            method: 'POST'
        });
    }

    /**
     * Restore from a backup
     */
    async restoreBackup(backupName, backupType) {
        return await this.request(`/api/backups/${encodeURIComponent(backupType)}/${encodeURIComponent(backupName)}`, {
            method: 'POST'
        });
    }

    /**
     * Delete a backup by its name
     */
    async deleteBackup(backupName, backupType) {
        return await this.request(`/api/backups/${encodeURIComponent(backupType)}/${encodeURIComponent(backupName)}`, {
            method: 'DELETE'
        });
    }

    // ==================== Agents Configuration API ====================

    async getAgentConfig() {
        try {
            return await this.request('/api/agents/config', { method: 'GET' });
        } catch (error) {
            if (error.message && error.message.includes('404')) {
                return null;
            }
            throw error;
        }
    }

    async saveAgentConfig(config) {
        return await this.request('/api/agents/config', {
            method: 'POST',
            body: JSON.stringify(config)
        });
    }

    async triggerPricingSync() {
        return await this.request('/api/pricing/scrape?run_in_background=true', {
            method: 'POST'
        });
    }

    async getSystemProviders() {
        try {
            const response = await this.request('/api/pricing/providers');
            return response.providers;
        } catch (error) {
            console.error('Error fetching system providers:', error);
            return null;
        }
    }
}

// Create global API client instance
const apiClient = new OmniContextAPI();

export { OmniContextAPI, apiClient };
